import { useCallback, useEffect, useState } from "react";
import { FileText, Send, Loader2, CheckCircle2, Clock, Download, FileArchive, Undo2, BookUser, BookOpenCheck, Mail } from "lucide-react";
import { api, API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { toast } from "sonner";
import CreditNoteDialog from "@/components/admin/CreditNoteDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtAmount = (v) => (v == null ? "—" : `€ ${String(v).replace(".", ",")}`);
const iso = (d) => d.toISOString().slice(0, 10);
const quarterStart = () => { const n = new Date(); return iso(new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1)); };

async function downloadAuth(url, filename) {
  const token = tokenStore.get();
  const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(formatApiError(j.detail) || "Download mislukt."); }
  const href = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

function ExportBar() {
  const [from, setFrom] = useState(quarterStart());
  const [to, setTo] = useState(iso(new Date()));
  const [busy, setBusy] = useState(null);
  const EXPORTS = {
    csv:      { path: "export.csv", file: (f, t) => `facturen_${f}_${t}.csv`, ok: "CSV gedownload — direct te openen in Excel." },
    zip:      { path: "export.zip", file: (f, t) => `facturen_${f}_${t}.zip`, ok: "Zip met alle factuur-PDF's gedownload." },
    mutaties: { path: "export-eboekhouden-mutaties.csv", file: (f, t) => `eboekhouden_mutaties_${f}_${t}.csv`, ok: "e-Boekhouden mutaties gedownload — importeer via Beheer › Inrichting › Mutaties importeren." },
    relaties: { path: "export-eboekhouden-relaties.csv", file: (f, t) => `eboekhouden_relaties_${f}_${t}.csv`, ok: "e-Boekhouden relaties gedownload — importeer via Relaties › Importeren (vóór de mutaties)." },
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      const x = EXPORTS[kind];
      await downloadAuth(`${API_BASE}/admin/invoices/${x.path}?date_from=${from}&date_to=${to}`, x.file(from, to));
      toast.success(x.ok);
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };
  const sendQuarter = async () => {
    setBusy("quarter");
    try {
      let { data } = await api.post("/admin/eboekhouden-quarter/send");
      if (data.already_sent && window.confirm(`Kwartaalmail ${data.quarter} is al verstuurd op ${fmtDate(data.sent_at)} naar ${data.to}. Opnieuw versturen?`)) {
        ({ data } = await api.post("/admin/eboekhouden-quarter/send", null, { params: { force: true } }));
      }
      if (data.already_sent) toast.info(`Kwartaalmail ${data.quarter} was al verstuurd — niets gedaan.`);
      else toast.success(`Kwartaalmail ${data.quarter} verstuurd naar ${data.to} (${data.summary.invoices} facturen).`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Versturen mislukt."); }
    setBusy(null);
  };
  const input = "border-2 border-foreground bg-background px-2 py-1 text-xs font-mono-num focus:outline-none focus:ring-2 focus:ring-primary";
  const btn = "btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";
  const Icon = ({ kind, fallback: Fallback }) => (busy === kind ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Fallback className="w-3.5 h-3.5" />);
  return (
    <div className="px-5 py-3 border-b-2 border-foreground bg-muted/30 flex items-center gap-3 flex-wrap" data-testid="invoice-export-bar">
      <span className="stat-label">Export boekhouding</span>
      <label className="text-xs inline-flex items-center gap-1">van <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="invoice-export-from" className={input} /></label>
      <label className="text-xs inline-flex items-center gap-1">tot en met <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="invoice-export-to" className={input} /></label>
      <button onClick={() => run("csv")} disabled={!!busy} data-testid="invoice-export-csv" className={btn}><Icon kind="csv" fallback={Download} /> CSV / Excel</button>
      <button onClick={() => run("zip")} disabled={!!busy} data-testid="invoice-export-zip" className={btn}><Icon kind="zip" fallback={FileArchive} /> Zip met PDF's</button>
      <span className="w-px h-6 bg-foreground/30 mx-1 hidden sm:inline-block" />
      <button onClick={() => run("relaties")} disabled={!!busy} data-testid="invoice-export-eb-relaties" title="Klanten (relaties) voor e-Boekhouden — importeer deze eerst"
        className={`${btn} !bg-primary !text-primary-foreground`}><Icon kind="relaties" fallback={BookUser} /> e-Boekhouden relaties</button>
      <button onClick={() => run("mutaties")} disabled={!!busy} data-testid="invoice-export-eb-mutaties" title="Verkoopfacturen (Factuur verstuurd, 1300/8000, HOOG_VERK_21) voor e-Boekhouden"
        className={`${btn} !bg-primary !text-primary-foreground`}><Icon kind="mutaties" fallback={BookOpenCheck} /> e-Boekhouden mutaties</button>
      <button onClick={sendQuarter} disabled={!!busy} data-testid="invoice-quarter-mail" title="Mailt de bestanden van het vorige kwartaal naar het beheer-adres (gaat ook automatisch op 1 jan/apr/jul/okt)"
        className={btn}><Icon kind="quarter" fallback={Mail} /> Kwartaalmail nu</button>
    </div>
  );
}

export default function InvoiceQueueBlock() {
  const [invoices, setInvoices] = useState(null);
  const [autoSend, setAutoSend] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [creditFor, setCreditFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inv, settings] = await Promise.all([api.get("/admin/invoices"), api.get("/admin/settings")]);
      setInvoices(inv.data.invoices || []);
      setAutoSend(!!settings.data.invoice_auto_send);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Laden mislukt.");
      setInvoices([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleAuto = async () => {
    setToggling(true);
    try {
      const { data } = await api.put("/admin/settings", { invoice_auto_send: !autoSend });
      setAutoSend(!!data.invoice_auto_send);
      toast.success(data.invoice_auto_send ? "Facturen gaan nu automatisch weg na betaling." : "Automatisch versturen staat uit — je controleert eerst zelf.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setToggling(false);
  };

  const openPdf = async (inv) => {
    setBusyId(`pdf-${inv.payment_id}`);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}/admin/invoices/${inv.payment_id}.pdf`, {
        credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(formatApiError(j.detail) || "PDF mislukt."); }
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message);
    }
    setBusyId(null);
  };

  const send = async (inv) => {
    const again = !!inv.invoice_sent_at;
    if (!window.confirm(`Factuur ${inv.invoice_number} ${again ? "opnieuw " : ""}versturen naar ${inv.customer_email}?`)) return;
    setBusyId(`send-${inv.payment_id}`);
    try {
      const { data } = await api.post(`/admin/invoices/${inv.payment_id}/send`);
      toast.success(`Factuur ${data.invoice_number} verstuurd naar ${data.sent_to}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Versturen mislukt.");
    }
    setBusyId(null);
  };

  const pending = (invoices || []).filter((i) => !i.invoice_sent_at).length;

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow overflow-x-auto" data-testid="invoice-queue-block">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Facturen</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Betaalde bestellingen. Bekijk de PDF en verstuur hem pas als hij klopt.
            {pending > 0 && <span className="ml-2 font-bold text-secondary" data-testid="invoice-pending-count">{pending} te versturen</span>}
          </p>
        </div>
        <button onClick={toggleAuto} disabled={toggling} data-testid="invoice-auto-send-toggle" aria-pressed={autoSend}
          className={`btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-2 disabled:opacity-50 ${autoSend ? "bg-positive text-black" : "bg-background text-foreground"}`}>
          <span className={`inline-block w-8 h-4 border-2 border-foreground relative ${autoSend ? "bg-foreground" : "bg-background"}`}>
            <span className={`absolute top-0 h-3 w-3 bg-background border border-foreground transition-transform ${autoSend ? "translate-x-4" : "translate-x-0"}`} />
          </span>
          Automatisch versturen: {autoSend ? "aan" : "uit"}
        </button>
      </div>
      <ExportBar />
      {invoices === null ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div>
      ) : invoices.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="invoice-queue-empty">Nog geen betaalde bestellingen.</div>
      ) : (
        <table className="w-full text-sm" data-testid="invoice-queue-table">
          <thead>
            <tr className="bg-foreground text-background">
              {["Datum", "Factuurnr", "Klant", "Plan", "Bedrag", "Status", "Acties"].map((h) => (
                <th key={h} className={`px-3 py-2 font-display uppercase text-xs tracking-widest ${h === "Acties" ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.payment_id} data-testid={`invoice-row-${inv.payment_id}`} className={inv.invoice_sent_at ? "bg-muted/40" : ""}>
                <td className="px-3 py-3 font-mono-num text-xs">{fmtDate(inv.date)}</td>
                <td className="px-3 py-3 font-mono-num font-bold">{inv.invoice_number}</td>
                <td className="px-3 py-3">
                  <div className="font-bold">{inv.customer_name}</div>
                  <div className="text-xs text-muted-foreground font-mono-num">{inv.customer_email}</div>
                  {inv.relation_code && <div className="text-[10px] font-mono-num tracking-widest text-primary font-bold" data-testid={`invoice-relation-${inv.payment_id}`} title="Relatiecode e-Boekhouden">{inv.relation_code}</div>}
                </td>
                <td className="px-3 py-3 text-xs uppercase font-bold">
                  {inv.plan}
                  {inv.kind === "renewal" && <span className="ml-1 px-1 py-0.5 bg-muted text-muted-foreground text-[10px] tracking-widest" data-testid={`invoice-renewal-${inv.payment_id}`}>Verlenging</span>}
                  {inv.kind === "credit_note" && <span className="ml-1 px-1 py-0.5 bg-destructive text-white text-[10px] tracking-widest" data-testid={`invoice-credit-${inv.payment_id}`}>Creditfactuur · {inv.credit_of_invoice_number}</span>}
                  {inv.credited_amount && inv.kind !== "credit_note" && <span className="ml-1 px-1 py-0.5 bg-secondary/20 text-secondary text-[10px] tracking-widest" data-testid={`invoice-credited-${inv.payment_id}`}>Gecrediteerd € {String(inv.credited_amount).replace(".", ",")}</span>}
                </td>
                <td className={`px-3 py-3 font-mono-num ${inv.kind === "credit_note" ? "text-destructive font-bold" : ""}`}>
                  {fmtAmount(inv.amount)}
                  {inv.discount?.percent && <span className="ml-1 text-[10px] text-secondary font-bold">−{inv.discount.percent}%</span>}
                </td>
                <td className="px-3 py-3 text-xs">
                  {inv.invoice_sent_at ? (
                    <span className="inline-flex items-center gap-1 text-positive font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> Verstuurd {fmtDate(inv.invoice_sent_at)}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-secondary font-bold"><Clock className="w-3.5 h-3.5" /> Te versturen</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openPdf(inv)} disabled={busyId === `pdf-${inv.payment_id}`} data-testid={`invoice-pdf-${inv.payment_id}`}
                      className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busyId === `pdf-${inv.payment_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Bekijk PDF
                    </button>
                    <button onClick={() => send(inv)} disabled={busyId === `send-${inv.payment_id}`} data-testid={`invoice-send-${inv.payment_id}`}
                      className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busyId === `send-${inv.payment_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {inv.invoice_sent_at ? "Opnieuw" : "Verstuur"}
                    </button>
                    {inv.kind !== "credit_note" && Number(inv.credited_amount || 0) < Number(inv.amount || 0) && (
                      <button onClick={() => setCreditFor(inv)} data-testid={`invoice-credit-btn-${inv.payment_id}`} title="Creditfactuur maken"
                        className="btn-brutal bg-background text-destructive px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
                        <Undo2 className="w-3.5 h-3.5" /> Crediteren
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <CreditNoteDialog invoice={creditFor} onClose={() => setCreditFor(null)} onDone={() => { setCreditFor(null); load(); }} />
    </section>
  );
}
